import { useRef, type Dispatch, type SetStateAction } from "react";
import type { Task, BoardColumn } from "../../types";
import type { ListlessGroupPlacement } from "../../lib/listlessGroupPlacement";
import { arrangeByPlacement } from "../../lib/listlessGroupPlacement";
import { useBoardColumnFlip } from "../../hooks/useBoardColumnFlip";
import BoardColumnComponent from "./BoardColumn";
import BoardListlessArea from "./BoardListlessArea";
import BoardMobileLayout from "./BoardMobileLayout";

interface BoardColumnLayoutProps {
  columns: BoardColumn[];
  listlessTasks: Task[];
  matchingTaskIds: Set<string>;
  listlessPlacement: ListlessGroupPlacement;
  isMobile: boolean;
  activeDragTask: Task | null;
  overTaskId: string | null;
  overEndZoneListId: string | null;
  exitAnimatingTaskId: string | null;
  focusedTaskId: string | null;
  isCollapsed: (listId: string | undefined) => boolean;
  // Present only while lists can collapse.
  onToggleCollapse?: (listId: string) => void;
  onAddToColumn: (columnName: string, listId?: string) => void;
  onToggleTask: (id: string) => void;
  onEditTask: (task: Task) => void;
  onConvertToSubtask: Dispatch<SetStateAction<string | null>>;
  onDeleteList: (listName: string) => void;
}

export default function BoardColumnLayout({
  columns,
  listlessTasks,
  matchingTaskIds,
  listlessPlacement,
  isMobile,
  activeDragTask,
  overTaskId,
  overEndZoneListId,
  exitAnimatingTaskId,
  focusedTaskId,
  isCollapsed,
  onToggleCollapse,
  onAddToColumn,
  onToggleTask,
  onEditTask,
  onConvertToSubtask,
  onDeleteList,
}: BoardColumnLayoutProps) {
  const columnContainerRef = useRef<HTMLDivElement>(null);
  useBoardColumnFlip({
    containerRef: columnContainerRef,
    columns,
    listlessTasks,
    matchingTaskIds,
    listlessPlacement,
    isCollapsed,
    overTaskId,
    overEndZoneListId,
  });

  const visibleColumns = columns.filter(
    (column) => column.tasks.some((t) => matchingTaskIds.has(t.id)),
  );

  const showListlessArea = listlessTasks.some((t) => matchingTaskIds.has(t.id));

  const listlessAreaProps = {
    tasks: listlessTasks,
    matchingTaskIds,
    activeDragTask,
    overTaskId,
    isOverEndZone: overEndZoneListId === "listless",
    exitAnimatingTaskId,
    focusedTaskId,
    onToggleTask,
    onEditTask,
    onConvertToSubtask,
  };

  const columnCards = visibleColumns.map((column) => {
    const { listId } = column;
    return (
      <BoardColumnComponent
        key={column.name}
        column={column}
        viewMode="list"
        matchingTaskIds={matchingTaskIds}
        activeDragTask={activeDragTask}
        overTaskId={overTaskId}
        isOverEndZone={overEndZoneListId === (listId ?? "inbox")}
        exitAnimatingTaskId={exitAnimatingTaskId}
        focusedTaskId={focusedTaskId}
        onAddToColumn={(name) => onAddToColumn(name, listId)}
        onToggleTask={onToggleTask}
        onEditTask={onEditTask}
        onConvertToSubtask={onConvertToSubtask}
        onDeleteList={onDeleteList}
        collapsed={isCollapsed(listId)}
        onToggleCollapse={
          onToggleCollapse && listId ? () => onToggleCollapse(listId) : undefined
        }
      />
    );
  });

  const listlessNodes =
    isMobile && !showListlessArea
      ? []
      : [
          <BoardListlessArea
            key="__listless__"
            layout={isMobile ? "mobile" : "desktop"}
            {...listlessAreaProps}
          />,
        ];

  const ordered = arrangeByPlacement(
    columnCards,
    listlessNodes,
    listlessPlacement,
  );

  if (isMobile) {
    return <BoardMobileLayout>{ordered}</BoardMobileLayout>;
  }

  return (
    <div
      ref={columnContainerRef}
      style={{ columnWidth: "280px", columnGap: "12px" }}
    >
      {ordered}
    </div>
  );
}
